import React, { createContext, useContext, useState } from "react";
import { isDevelopment } from "../extensions/environments";
import { useSiteConfig } from "./SiteConfigContext";

export enum FeatureToggle {
  PaymentProcess = "PaymentProcess",
  CustomPaymentForm = "CustomPaymentForm",
  PaymentProcessDonation = "PaymentProcessDonation",
}

interface FeatureToggleContextValue {
  isFeatureEnabled: (
    key: FeatureToggle,
    developmentEnabled?: boolean,
  ) => boolean;
  setFeatureEnabled: (key: FeatureToggle) => void;
}

const FeatureToggleContext = createContext<FeatureToggleContextValue | null>(
  null,
);

export const FeatureToggleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isFeatureToggleEnabled, setIsFeatureToggleEnabled] = useState<{
    [key: string]: boolean;
  }>({
    // Checkout is LIVE by default. It was previously gated behind
    // `?paymentprocess=enabled` while Printful sync_variant_ids were out of sync;
    // that SKU-sync bug is fixed (see scripts/sync-printful-skus.js) so public
    // checkout is re-enabled. PREREQUISITE: the PRODUCTION Square SKUs must have
    // been re-synced (`SQUARE_ENVIRONMENT=production node scripts/sync-printful-skus.js --apply`)
    // before going live — otherwise every order fails to create its Printful
    // draft and is voided at checkout. To kill checkout again without a deploy,
    // flip this back to `false`. Dev/localhost is unaffected.
    [FeatureToggle.PaymentProcess]: true,

    [FeatureToggle.CustomPaymentForm]: true,
    [FeatureToggle.PaymentProcessDonation]: false,
  });

  // Server-driven checkout kill switch. It only ever force-DISABLES: when the
  // admin flips checkout off, `checkoutEnabled` is `false` and PaymentProcess is
  // reported off everywhere (dev included, so a preview reflects the real state).
  // Until the config loads — or if it fails — it's undefined and we keep the
  // hardcoded default (ON), so a config blip can never take checkout down.
  const { checkoutEnabled } = useSiteConfig();

  const isFeatureEnabled = (
    key: FeatureToggle,
    developmentEnabled: boolean = true,
  ): boolean => {
    if (key === FeatureToggle.PaymentProcess && checkoutEnabled === false) {
      return false;
    }
    return (
      // Always allow feature to be enabled in development
      isFeatureToggleEnabled[key] || (developmentEnabled && isDevelopment())
    );
  };

  const setFeatureEnabled = (key: FeatureToggle) => {
    setIsFeatureToggleEnabled((ftd) => ({ ...ftd, [key]: true }));
  };

  return (
    <FeatureToggleContext.Provider
      value={{ isFeatureEnabled, setFeatureEnabled }}
    >
      {children}
    </FeatureToggleContext.Provider>
  );
};

export const useFeatureToggle = () => {
  const context = useContext(FeatureToggleContext);
  if (!context) {
    throw new Error(
      "useFeatureToggle must be used within a FeatureToggleProvider",
    );
  }
  return context;
};
